/**
 * Listing import from a spreadsheet: the seller pastes rows (or uploads a CSV) with the card, set,
 * number, condition, language, finish, grading, price and quantity. This reads the sheet (tab, ";"
 * or "," separated, Spanish or English headers, Argentine prices) into rows with their errors; the
 * server then finds each row's catalog product and the seller confirms before anything is created.
 */
import { GRADERS, isSealedCategory, MARKET_CONDITIONS, MARKET_FINISHES, MARKET_LANGUAGES, type Grader, type ListingCondition, type MarketFinish } from "./market";
import { gameFinishes } from "./games";

/** Rows per import; a bigger sheet is split in parts. */
export const IMPORT_MAX_ROWS = 300;
/** Characters of pasted text the server reads (300 rows with long notes fit). */
export const IMPORT_MAX_CHARS = 400_000;

export type ImportField = "card" | "set" | "number" | "game" | "condition" | "language" | "finish" | "grade" | "price" | "quantity" | "notes";

/**
 * Problems with a row: a field that can't be read, or (after matching) "condition_missing" for a
 * single without condition, "single_sealed" for a single marked sealed, "sealed_graded" for a graded
 * sealed product, "card" when there is neither a name nor a set and number.
 */
export type ImportError = Exclude<ImportField, "set" | "number" | "notes"> | "condition_missing" | "single_sealed" | "sealed_graded" | "finish_game";

/** What a row says, read and checked; condition stays null when blank (sealed products don't need it). */
export interface ImportDraft {
  condition: ListingCondition | null;
  language: string;
  finish: MarketFinish;
  grader: Grader | null;
  grade: number | null;
  priceArs: number | null;
  quantity: number;
  notes: string;
}

export interface ImportRow {
  /** Spreadsheet row number (the header is row 1). */
  line: number;
  card: string;
  set: string;
  number: string;
  /** The game as written ("One Piece", "op", "pokemon"); blank: any open game. The server reads it. */
  game: string;
  draft: ImportDraft;
  errors: ImportError[];
}

export type SheetError = "empty" | "no_header" | "too_many";

/** A catalog product a row may be. */
export interface ImportCandidate {
  productId: number;
  name: string;
  setName: string | null;
  cardNumber: string | null;
  imageKey: string;
  category: string;
  /** Its game (the finishes a listing of it can have, shared/games.ts). */
  game: string;
}

export type ImportStatus = "ok" | "ambiguous" | "not_found" | "invalid";

export interface ImportPreviewRow extends ImportRow {
  status: ImportStatus;
  /** ok: the one product; ambiguous: the seller picks one; else empty. */
  candidates: ImportCandidate[];
}

export interface ImportPreview {
  rows: ImportPreviewRow[];
  /** listingKey() of the seller's current listings, to flag rows already published. */
  existingKeys: string[];
  /** Live listings now and how many the seller can have live. */
  live: number;
  /** Live listings the plan allows (null: no limit). */
  maxLive: number | null;
}

/** One listing to create, as the commit endpoint takes it. */
export interface ImportItem {
  line: number;
  productId: number;
  condition: ListingCondition;
  language: string;
  finish: MarketFinish;
  grader: Grader | null;
  grade: number | null;
  priceArs: number;
  quantity: number;
  notes: string;
}

export interface ImportResult {
  created: number;
  published: number;
  /** Created paused: the live-listing limit was reached. */
  overLimit: number;
  /** Created paused, ready: they go live when the seller's request is approved. */
  awaitingApproval: number;
  /** Live listings the plan allows (null: no limit). */
  maxLive: number | null;
}

/** Same product in the same condition, language, finish and grading. */
export const listingKey = (l: { productId: number; condition: string; language: string; finish: string; grader: string | null; grade: number | null }) =>
  [l.productId, l.condition, l.language, l.finish, l.grader ?? "", l.grade ?? ""].join("|");

// ---- Reading the sheet ---------------------------------------------------------------------------

/** Lowercase, no accents, letters and digits only ("N°" → "n", "Terminación" → "terminacion"). */
const norm = (s: string) =>
  s
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const HEADERS: Record<ImportField, string[]> = {
  card: ["carta", "nombre", "card", "name", "producto", "product", "nombrecarta", "cardname"],
  set: ["set", "expansion", "edicion", "setname", "codigoset", "setcode"],
  number: ["numero", "nro", "n", "num", "no", "number", "cardnumber", "numerocarta"],
  game: ["juego", "game", "tcg", "juegodecartas"],
  condition: ["estado", "condicion", "condition", "cond"],
  language: ["idioma", "language", "lang", "lengua"],
  finish: ["terminacion", "acabado", "finish", "foil", "version"],
  grade: ["gradeo", "grado", "grade", "graded", "gradeada", "graduacion"],
  price: ["precio", "price", "precioars", "preciopesos", "valor"],
  quantity: ["cantidad", "cant", "qty", "quantity", "stock", "unidades"],
  notes: ["notas", "nota", "notes", "comentario", "comentarios", "observaciones", "detalle"],
};
const HEADER_OF = new Map<string, ImportField>(Object.entries(HEADERS).flatMap(([field, names]) => names.map((n) => [n, field as ImportField])));

const CONDITIONS: Record<string, ListingCondition> = {};
const addAll = <T>(map: Record<string, T>, value: T, names: string[]) => names.forEach((n) => (map[norm(n)] = value));
addAll(CONDITIONS, "NM", ["nm", "near mint", "mint", "nm/m", "nm-m", "m", "perfecta", "perfecto", "nueva", "nuevo", "impecable"]);
addAll(CONDITIONS, "LP", ["lp", "lightly played", "ex", "excellent", "excelente", "poco jugada", "ligeramente jugada", "casi nueva"]);
addAll(CONDITIONS, "MP", ["mp", "moderately played", "gd", "good", "buena", "jugada", "moderadamente jugada"]);
addAll(CONDITIONS, "HP", ["hp", "heavily played", "pl", "played", "muy jugada", "bastante jugada"]);
addAll(CONDITIONS, "DMG", ["dmg", "damaged", "dañada", "danada", "poor", "po", "rota"]);
addAll(CONDITIONS, "SEALED", ["sellado", "sellada", "sealed", "cerrado", "cerrada"]);

const LANGUAGES: Record<string, string> = {};
addAll(LANGUAGES, "es", ["es", "esp", "spa", "español", "espanol", "spanish", "castellano"]);
addAll(LANGUAGES, "en", ["en", "eng", "ing", "inglés", "ingles", "english"]);
addAll(LANGUAGES, "ja", ["ja", "jp", "jap", "jpn", "japonés", "japones", "japanese"]);
addAll(LANGUAGES, "pt", ["pt", "por", "portugués", "portugues", "portuguese"]);
addAll(LANGUAGES, "fr", ["fr", "fra", "francés", "frances", "french"]);
addAll(LANGUAGES, "de", ["de", "ger", "deu", "alemán", "aleman", "german"]);
addAll(LANGUAGES, "it", ["it", "ita", "italiano", "italian"]);
addAll(LANGUAGES, "ko", ["ko", "kr", "kor", "coreano", "korean"]);
addAll(LANGUAGES, "zh", ["zh", "cn", "chi", "chino", "chinese"]);

const FINISHES: Record<string, MarketFinish> = {};
addAll(FINISHES, "normal", ["normal", "común", "comun", "no holo", "non holo", "nonholo", "regular", "standard"]);
addAll(FINISHES, "holo", ["holo", "holográfica", "holografica", "foil", "holofoil"]);
addAll(FINISHES, "reverse", ["reverse", "reverse holo", "reversa", "rh", "reverso", "reverse foil"]);
addAll(FINISHES, "first_edition", ["1st edition", "first edition", "1st", "primera edición", "primera edicion", "1ra edición", "1ra edicion", "1ed", "1a edicion"]);

// Every value maps to something the listing form accepts.
for (const c of Object.values(CONDITIONS)) if (c !== "SEALED" && !MARKET_CONDITIONS.includes(c)) throw new Error(c);
for (const l of Object.values(LANGUAGES)) if (!(MARKET_LANGUAGES as readonly string[]).includes(l)) throw new Error(l);
for (const f of Object.values(FINISHES)) if (!MARKET_FINISHES.includes(f)) throw new Error(f);

/** Whole pesos as Argentines write them: "$ 1.500", "1500", "1.500,00" (decimals other than zero are refused). */
export function parsePriceArs(raw: string): number | null {
  const s = raw.replace(/ars|\$|\s| /gi, "");
  let digits: string | null = null;
  if (/^\d{1,3}(\.\d{3})+(,0+)?$/.test(s)) digits = s.replace(/,0+$/, "").replace(/\./g, "");
  else if (/^\d+(,0+|\.0{1,2})?$/.test(s)) digits = s.replace(/[.,]0+$/, "");
  if (digits === null) return null;
  const n = Number(digits);
  return Number.isSafeInteger(n) && n >= 1 && n <= 100_000_000 ? n : null;
}

/** "PSA 10", "bgs 9.5", "CGC 9,5", "Beckett 9.5" (BGS); blank is ungraded. */
function parseGrade(raw: string): { grader: Grader | null; grade: number | null } | null {
  if (!raw.trim()) return { grader: null, grade: null };
  const m = /^([a-z]+)\s*-?\s*(\d{1,2}(?:[.,]5)?)$/i.exec(raw.trim());
  const grader = m?.[1].toUpperCase() === "BECKETT" ? "BGS" : m?.[1].toUpperCase();
  const grade = m ? Number(m[2].replace(",", ".")) : NaN;
  if (!m || !(GRADERS as readonly string[]).includes(grader!) || grade < 1 || grade > 10) return null;
  return { grader: grader as Grader, grade };
}

/** Splits CSV-like text on the separator, with "quoted ""fields""" that may hold separators and line breaks. */
export function splitRecords(text: string, sep: string): string[][] {
  const records: string[][] = [];
  let row: string[] = [];
  let field = "";
  let quoted = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (quoted) {
      if (ch === '"' && text[i + 1] === '"') {
        field += '"';
        i++;
      } else if (ch === '"') quoted = false;
      else field += ch;
    } else if (ch === '"' && field.trim() === "") {
      field = "";
      quoted = true;
    } else if (ch === sep) {
      row.push(field);
      field = "";
    } else if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && text[i + 1] === "\n") i++;
      row.push(field);
      records.push(row);
      row = [];
      field = "";
    } else field += ch;
  }
  if (field !== "" || row.length) {
    row.push(field);
    records.push(row);
  }
  return records;
}

/** The header line's separator: tab (pasted from a spreadsheet), else ";" (Excel in Spanish), else ",". */
function detectSeparator(text: string): string {
  const first = text.split(/\r?\n/).find((l) => l.trim()) ?? "";
  const count = (c: string) => first.split(c).length - 1;
  const [tab, semi, comma] = [count("\t"), count(";"), count(",")];
  if (tab > 0 && tab >= semi && tab >= comma) return "\t";
  return semi > 0 && semi >= comma ? ";" : ",";
}

/** Reads the sheet: its header names the columns (any order, Spanish or English); blank rows are skipped. */
export function parseImportSheet(text: string): { ok: true; rows: ImportRow[] } | { ok: false; error: SheetError } {
  const clean = text.replace(/^﻿/, "");
  if (!clean.trim()) return { ok: false, error: "empty" };
  const records = splitRecords(clean, detectSeparator(clean));
  const headerAt = records.findIndex((r) => r.some((c) => c.trim()));
  const columns = records[headerAt].map((h) => HEADER_OF.get(norm(h)) ?? null);
  if (!columns.includes("price") || (!columns.includes("card") && !(columns.includes("set") && columns.includes("number")))) return { ok: false, error: "no_header" };

  const rows: ImportRow[] = [];
  for (let i = headerAt + 1; i < records.length; i++) {
    const cells = records[i];
    if (!cells.some((c) => c.trim())) continue;
    if (rows.length === IMPORT_MAX_ROWS) return { ok: false, error: "too_many" };
    const get = (f: ImportField) => {
      const at = columns.indexOf(f);
      return at < 0 ? "" : (cells[at] ?? "").trim();
    };
    rows.push(readRow(i + 1, get));
  }
  return rows.length ? { ok: true, rows } : { ok: false, error: "empty" };
}

function readRow(line: number, get: (f: ImportField) => string): ImportRow {
  const errors: ImportError[] = [];
  const pick = <T>(field: ImportError, raw: string, map: Record<string, T>, blank: T): T => {
    if (!raw) return blank;
    const v = map[norm(raw)];
    if (v === undefined) errors.push(field);
    return v ?? blank;
  };
  const card = get("card").slice(0, 120);
  const set = get("set").slice(0, 80);
  const number = get("number").slice(0, 20);
  const game = get("game").slice(0, 40);
  if (!card && !(set && number)) errors.push("card");

  const condition = pick<ListingCondition | null>("condition", get("condition"), CONDITIONS, null);
  const language = pick("language", get("language"), LANGUAGES, "es");
  const finish = pick("finish", get("finish"), FINISHES, "normal");
  const graded = parseGrade(get("grade"));
  if (!graded) errors.push("grade");
  const priceArs = parsePriceArs(get("price"));
  if (priceArs === null) errors.push("price");
  const rawQty = get("quantity");
  const quantity = rawQty === "" ? 1 : /^\d{1,3}$/.test(rawQty) && Number(rawQty) >= 1 ? Number(rawQty) : null;
  if (quantity === null) errors.push("quantity");

  return {
    line,
    card,
    set,
    number,
    game,
    draft: { condition, language, finish, grader: graded?.grader ?? null, grade: graded?.grade ?? null, priceArs, quantity: quantity ?? 1, notes: get("notes").slice(0, 1000) },
    errors,
  };
}

/**
 * The listing a row makes for a product of this category: sealed products are always "SEALED",
 * plain and ungraded (a condition written for one is ignored); singles need their own condition and
 * a finish their game has ("reverse" on a One Piece card is a mistake, not a plain listing).
 */
export function itemFor(row: Pick<ImportRow, "line" | "draft">, productId: number, category: string, game: string): { ok: true; item: ImportItem } | { ok: false; error: ImportError } {
  const d = row.draft;
  if (d.priceArs === null) return { ok: false, error: "price" };
  const base = { line: row.line, productId, language: d.language, priceArs: d.priceArs, quantity: d.quantity, notes: d.notes };
  if (isSealedCategory(category)) {
    if (d.grader) return { ok: false, error: "sealed_graded" };
    return { ok: true, item: { ...base, condition: "SEALED", finish: "normal", grader: null, grade: null } };
  }
  if (d.condition === null) return { ok: false, error: "condition_missing" };
  if (d.condition === "SEALED") return { ok: false, error: "single_sealed" };
  if (!gameFinishes(game).includes(d.finish)) return { ok: false, error: "finish_game" };
  return { ok: true, item: { ...base, condition: d.condition, finish: d.finish, grader: d.grader, grade: d.grade } };
}

/** The downloadable template: Excel in Spanish opens ";" files with the BOM as UTF-8. */
export const IMPORT_TEMPLATE = [
  ["Carta", "Set", "Número", "Estado", "Idioma", "Terminación", "Gradeo", "Precio", "Cantidad", "Notas"],
  ["Pikachu", "Scarlet & Violet 151", "025/165", "NM", "Inglés", "Normal", "", "$ 2.500", "2", ""],
  ["Charizard ex", "Obsidian Flames", "125/197", "NM", "Inglés", "Holo", "PSA 10", "$ 180.000", "1", "Slab sin marcas"],
  ["Elite Trainer Box Prismatic Evolutions", "", "", "Sellado", "Inglés", "", "", "$ 95.000", "1", ""],
]
  .map((r) => r.map((c) => (/[;"\n]/.test(c) ? `"${c.replace(/"/g, '""')}"` : c)).join(";"))
  .join("\r\n");
